import { KeyboardEvent, ReactElement, useCallback, useMemo, useState } from 'react';
import { Pagination } from '@components/Pagination';
import TableCore, { buildColGroup } from './TableCore';
import {
  BodyRow,
  PagedTableViewport,
  StyledTable,
  TablePagination,
  TablePaginationCount,
} from './styles';
import { PagedServerTableProps, TableResponse } from './types';
import useServerTable from './useServerTable';

const PagedServerTable = <Row,>(props: PagedServerTableProps<Row>): ReactElement => {
  const {
    columns,
    getRowKey,
    getRowStyle,
    itemLabel,
    onRowClick,
    pageSize = 50,
    selectedRowKey,
  } = props;
  const [rows, setRows] = useState<Row[]>([]);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const resetRows = useCallback(() => {
    setRows([]);
    setPage(0);
  }, []);
  const handleResponse = useCallback((response: TableResponse<Row>, requestedPage: number) => {
    setRows(response.items);
    setTotalItems(response.totalItems ?? response.items.length);
    setPage(requestedPage);
  }, []);
  const table = useServerTable({
    ...props,
    onReset: resetRows,
    onResponse: handleResponse,
  });

  const changePage = useCallback((nextPage: number) => {
    table.cancelPendingFilterLoad();
    table.loadPage(nextPage - 1, true);
  }, [table.cancelPendingFilterLoad, table.loadPage]);

  const renderBody = useCallback((renderRow: (index: number, row: Row) => ReactElement) => (
    <PagedTableViewport pageSize={pageSize}>
      <StyledTable>
        {buildColGroup(columns)}
        <tbody>
          {rows.map((row, index) => {
            const rowKey = getRowKey(row);
            const isSelected = selectedRowKey !== undefined && rowKey === selectedRowKey;
            const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
              if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                onRowClick(row);
              }
            };
            return (
              <BodyRow
                key={rowKey}
                clickable={Boolean(onRowClick)}
                selected={isSelected}
                style={getRowStyle?.(row)}
                aria-selected={isSelected || undefined}
                onClick={() => onRowClick?.(row)}
                onKeyDown={handleKeyDown}
                tabIndex={onRowClick ? 0 : undefined}
              >
                {renderRow(index, row)}
              </BodyRow>
            );
          })}
        </tbody>
      </StyledTable>
    </PagedTableViewport>
  ), [columns, getRowKey, getRowStyle, onRowClick, pageSize, rows, selectedRowKey]);

  const footer = useMemo(() => totalItems > 0 && (
    <TablePagination>
      <TablePaginationCount>{`${totalItems} ${itemLabel ?? 'items'}`}</TablePaginationCount>
      <Pagination
        count={Math.ceil(totalItems / pageSize)}
        page={page + 1}
        defaultPage={1}
        onChange={changePage}
      />
    </TablePagination>
  ), [changePage, itemLabel, page, pageSize, totalItems]);

  return (
    <TableCore
      {...props}
      {...table}
      rowsLength={rows.length}
      renderBody={renderBody}
      footer={footer}
    />
  );
};

export default PagedServerTable;
